import React, { useCallback, useMemo, useRef } from 'react';

import { Box, IconVariant, VStack } from '@rbilabs/universal-components';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useStoreContext } from 'features/store';
import { SIMPLIFIED_TWELVE_HOUR_TIME_PARSE_FORMAT } from 'features/store/constants';
import { useGetStoreDetails } from 'features/store/hooks/use-get-store-details';
import { useIsMobileOrderingAvailable } from 'features/store/hooks/use-is-mobile-ordering-available';
import { useSelectStore } from 'features/store/hooks/use-select-store';
import { useStoreAvailabilityFns } from 'features/store/hooks/use-store-availability-fns';
import { NonTraditionalStoreDialogDrawer } from 'features/store/pages/pickup-store-selector/components/non-traditional-store-dialog-drawer';
import { useNonTraditionalStoreUI } from 'features/store/pages/pickup-store-selector/hooks/use-non-traditional-store';
import { getStoreLocalTime, getUSState, readableTimeInterval } from 'features/store/utils';
import { StyleSheet, View } from 'react-native';

import { ILocation } from '@rbi-ctg/frontend';
import ActionButton from 'components/action-button';
import { CardModal } from 'components/card-modal';
import CloseButton from 'components/close-button';
import { Helmet } from 'components/helmet';
import Picture from 'components/picture';
import StaticMap from 'components/static-map';
import { ServiceMode } from 'generated/graphql-gateway';
import useErrorModal from 'hooks/use-error-modal';
import LoyaltyLoader from 'pages/loyalty/loyalty-loader/loyalty-loader';
import { useGeolocation } from 'state/geolocation';
import { t } from 'state/intl/translations';
import { TranslationKey } from 'state/intl/types';
import { useOrderContext } from 'state/order';
import { primitive } from 'styles/constants/primitives';
import { hiddenAccessibilityPlatformProps } from 'utils/accessibility';
import { routes } from 'utils/routing';
import { getStoreSEOSlug } from 'utils/slugify';

import { DirectionsLink } from './directions-link';
import StoreMetaForSEO from './meta';
import {
  Address,
  Amenity,
  AmenityIcon,
  FeatureIcon,
  HeaderContainer,
  InnerHeader,
  InnerText,
  PhoneNumber,
  SpacingLine,
  StoreHourContainer,
} from './styled';
import { IHoursProps, IStoreInfoModalProps } from './types';

const Hours = ({ title, hours }: IHoursProps) => {
  const hoursOfOperation = readableTimeInterval({
    fallbackMessage: t('hoursOfOperationFallbackMessage'),
    open24HoursMessage: t('open24Hours'),
    timeFormat: SIMPLIFIED_TWELVE_HOUR_TIME_PARSE_FORMAT,
  });

  const storeHoursSummary: [string, string][] = [
    ['monday', 'mon'],
    ['tuesday', 'tue'],
    ['wednesday', 'wed'],
    ['thursday', 'thr'],
    ['friday', 'fri'],
    ['saturday', 'sat'],
    ['sunday', 'sun'],
  ];

  if (!hours) {
    return null;
  }

  return (
    <Box>
      <InnerHeader variant="headerThree" marginY="$2">
        {title}
      </InnerHeader>
      {storeHoursSummary.map(([messageId, abbreviatedDay]) => {
        const openingHours = hours[`${abbreviatedDay}Open`];
        const closingHours = hours[`${abbreviatedDay}Close`];
        return (
          <StoreHourContainer key={messageId}>
            <InnerText>{t(messageId as any)}</InnerText>
            <InnerText>{hoursOfOperation(openingHours, closingHours)}</InnerText>
          </StoreHourContainer>
        );
      })}
    </Box>
  );
};

export const StoreInfoModal = ({ store }: IStoreInfoModalProps) => {
  const {
    name: storeName,
    phoneNumber,
    restaurantImage,
    latitude: lat,
    longitude: lng,
    physicalAddress,
  } = store;
  const { StoreChangeDialog } = useStoreContext();
  const nonTraditionalStoreUI = useNonTraditionalStoreUI({ restaurant: store });
  const router = useRouter();

  const { current: position } = useRef({ lat, lng });
  const { activeCoordinates } = useGeolocation();

  const {
    fetchingPosData,
    reorder: { reordering },
  } = useOrderContext();
  const selectStore = useSelectStore();

  const streetAddress = physicalAddress?.address1;
  const formattedCity = physicalAddress?.city;
  const state = getUSState(physicalAddress?.stateProvince ?? '');
  const postalCode = physicalAddress?.postalCode?.split('-')[0];
  const {
    isOpenForDelivery,
    isOpenForPickup,
    getStoreServiceModeStatuses,
    isMobileOrderingAvailable,
    isStoreAvailableForPickup,
    isServiceModeCapable,
  } = useStoreAvailabilityFns();
  const shouldShowWaysToOrderSection =
    isMobileOrderingAvailable(store) || isOpenForDelivery(store) || isOpenForPickup(store);

  const canonicalUrl = [`${routes.store}`, getStoreSEOSlug(store)].join('/');
  const serviceModeStatuses = getStoreServiceModeStatuses(store);
  const storeLocalTime = getStoreLocalTime();

  const shouldShowDineIn = isServiceModeCapable(store, ServiceMode.EAT_IN, storeLocalTime);
  const shouldShowTakeout = isServiceModeCapable(store, ServiceMode.TAKEOUT, storeLocalTime);
  const shouldShowMobileOrderDriveThru = isServiceModeCapable(
    store,
    ServiceMode.MOBILE_ORDER_DRIVE_THRU,
    storeLocalTime
  );
  const shouldShowDriveThru = isServiceModeCapable(store, ServiceMode.DRIVE_THRU, storeLocalTime);
  const isDeliveryAvailable = !!serviceModeStatuses.find(
    sms => sms.serviceMode === ServiceMode.DELIVERY
  )?.available;

  const amenitiesBreakdown: [IconVariant, boolean, string, TranslationKey][] = [
    ['mobile', useIsMobileOrderingAvailable(store), '22px', 'mobileOrderingAndPickup'],
    ['delivery', isDeliveryAvailable, '22px', 'delivery'],
  ];

  const storeHoursBreakdown = [
    [shouldShowDineIn || shouldShowTakeout, 'dineInHours', 'diningRoomHours'],
    [shouldShowDriveThru, 'driveThruHours', 'driveThruHours'],
    [shouldShowMobileOrderDriveThru, 'mobileOrderDriveThruHours', 'mobileOrderDriveThruHours'],
  ];

  const isStoreAvailable = isStoreAvailableForPickup(store);

  const orderButtonEnabled = isStoreAvailable && !fetchingPosData;

  const amenitiesSection = useMemo(() => {
    if (!store.amenities?.length) {
      return null;
    }

    const amenitiesWithIconSrc = store.amenities.filter(amenity => amenity?.icon?.asset?.url);

    if (!amenitiesWithIconSrc.length) {
      return null;
    }

    return (
      <VStack testID="restaurant-amenities">
        <InnerHeader accessibilityLevel={2} variant="headerThree" testID="in-store-amenities">
          {t('inStoreAmenities')}
        </InnerHeader>
        {amenitiesWithIconSrc.map(amenity => {
          const name = amenity?.name?.en ?? '';
          return (
            <Amenity key={name}>
              <FeatureIcon src={amenity?.icon?.asset?.url ?? undefined} alt={name} />
              <InnerText>{name}</InnerText>
            </Amenity>
          );
        })}
      </VStack>
    );
  }, [store]);

  const handleOnSelectStore = useCallback(async () => {
    if (nonTraditionalStoreUI?.isNonTraditionalStore) {
      nonTraditionalStoreUI.openDrawer();
      return;
    }
    await selectStore({
      newStore: store,
      requestedServiceMode: ServiceMode.DRIVE_THRU,
    });
  }, [nonTraditionalStoreUI, selectStore, store]);

  const handleOnConfirmNonTraditionalStoreDialog = async () => {
    await selectStore({
      newStore: store,
      requestedServiceMode: ServiceMode.DRIVE_THRU,
    });
    nonTraditionalStoreUI?.closeDrawer();
  };

  // Loading state covers two scenarios:
  // - fetchingPosData: Loading store POS connectivity/menu data for regular ordering
  // - reordering: Processing a reorder request (users may be redirected here to select a store if no store selected)
  const isLoading = fetchingPosData || reordering;

  return (
    <CardModal
      testID="store-info-modal"
      analyticsEvent="Store Details"
      scrollView
      standardPadding
      _headerOverride={
        <>
          <View style={styles.closeButton}>
            <CloseButton
              accessible
              accessibilityRole="button"
              accessibilityLabel={t('closeRestaurantInfoAccessibilityLabel')}
              onPress={() => router.dismiss()}
            />
          </View>
          <HeaderContainer>
            {restaurantImage ? (
              <Picture image={restaurantImage} alt={storeName ?? ''} />
            ) : (
              <StaticMap
                initialZoomIOS={8000}
                position={position as ILocation}
                title={storeName ?? physicalAddress?.address1 ?? undefined}
              />
            )}
          </HeaderContainer>
        </>
      }
      footer={
        isStoreAvailable && (
          <ActionButton
            isLoading={isLoading}
            testID="store-modal-order-here"
            fullWidth
            disabled={!orderButtonEnabled}
            onPress={handleOnSelectStore}
          >
            {t('orderHere')}
          </ActionButton>
        )
      }
    >
      {/* @ts-ignore */}
      <Helmet testID="canonical" link={[{ rel: 'canonical', href: canonicalUrl }]} />
      <InnerHeader
        accessibilityLevel={1}
        variant="headerTwo"
        testID="address"
        nativeID="modal-heading"
      >
        {streetAddress?.toLowerCase()}
      </InnerHeader>
      <Box>
        <DirectionsLink activeCoordinates={activeCoordinates} mapPosition={position as ILocation} />
        <Address>{`${formattedCity}${state ? ` ${state}` : ''}, ${postalCode}`}</Address>
        {phoneNumber && (
          <PhoneNumber
            accessibilityLabel={`${t('phoneNumber')} ${phoneNumber}`}
            testID="phoneNumber"
            to={`tel:${phoneNumber}`}
          >
            {phoneNumber}
          </PhoneNumber>
        )}
      </Box>
      <SpacingLine marginTop="$6" bottom="$3" />

      {shouldShowWaysToOrderSection && (
        <InnerHeader accessibilityLevel={2} variant="headerThree">
          {t('waysToOrder')}
        </InnerHeader>
      )}
      {amenitiesBreakdown.map(
        ([icon, shouldShow, height, messageId]) =>
          shouldShow && (
            <Amenity key={messageId}>
              <AmenityIcon
                variant={icon}
                width="40px"
                height={height}
                {...hiddenAccessibilityPlatformProps}
              />
              <InnerText>{t(messageId)}</InnerText>
            </Amenity>
          )
      )}
      {shouldShowWaysToOrderSection && <SpacingLine marginTop="$1" marginBottom="$4" />}

      {amenitiesSection}
      {storeHoursBreakdown.map(
        ([shouldShow, messageId, hours = messageId]: [boolean, TranslationKey, TranslationKey]) =>
          shouldShow && <Hours key={messageId} title={t(messageId)} hours={store[hours]} />
      )}
      <StoreMetaForSEO
        atCopy={t('at')}
        streetAddress={streetAddress ?? ''}
        city={formattedCity ?? ''}
        state={state}
        postalCode={postalCode ?? ''}
        phoneNumber={phoneNumber ?? ''}
        image={(restaurantImage as any) ?? undefined}
      />
      <StoreChangeDialog heading={t('changeStores')} />
      {nonTraditionalStoreUI && (
        <NonTraditionalStoreDialogDrawer
          onConfirm={handleOnConfirmNonTraditionalStoreDialog}
          onCancel={nonTraditionalStoreUI.closeDrawer}
          isOpen={nonTraditionalStoreUI.isDialogDrawerOpen}
          accessInstructions={nonTraditionalStoreUI.accessInstructions}
        />
      )}
    </CardModal>
  );
};

const StoreInfoModalContainer = () => {
  const { id } = useLocalSearchParams<{ id?: string }>();
  const { restaurant, error, loading } = useGetStoreDetails(id);
  const [ErrorDialog] = useErrorModal({
    init: true,
    modalAppearanceEventMessage: 'Error: Fetching Restaurant Info Failure',
  });

  if (error) {
    return <ErrorDialog />;
  }

  const isLoading = loading || !restaurant;

  if (isLoading) {
    return (
      <CardModal>
        <LoyaltyLoader />
      </CardModal>
    );
  }

  return <StoreInfoModal store={restaurant} />;
};

const styles = StyleSheet.create({
  closeButton: {
    position: 'absolute',
    top: 16,
    right: 16,
    zIndex: 1,
    backgroundColor: primitive.$white,
    borderRadius: 20,
  },
});

export default StoreInfoModalContainer;
